import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames/bind';
import history from '../../history';
import { getActiveCompany } from '../../selectors/company';
import Card from '../Card';
import Loading from '../Loading';
import Pagination from '../Pagination';
import { useCardData } from './useCardData';
import { BsCaretRightFill } from 'react-icons/bs';

import styles from './headerCards.module.scss';
const bStyles = classnames.bind(styles);

export default function FavoritesCard({ card, className }) {
  const company = useSelector(getActiveCompany);
  const [page, setPage] = useState(0);

  const data = useCardData(card);
  const perPage = 4;
  const sliced = useMemo(
    () => (data ?? []).slice(page * perPage, (page + 1) * perPage),
    [data, page]
  );

  const getGroupNames = (groupIds = []) => {
    if (!company?.groups?.length || !groupIds?.length) return '';
    const set = new Set(groupIds.map(String));
    return company.groups
      .filter(g => set.has(String(g._id)))
      .map(g => g.name)
      .join(', ');
  };

  const getProjectNames = (groupIds = [], projectIds = []) => {
    if (!company.groups?.length || !projectIds?.length) return '';
    const gset = new Set(groupIds.map(String));
    const pset = new Set(projectIds.map(String));
    const names = [];
    for (const g of company.groups) {
      if (!gset.size || gset.has(String(g._id))) {
        for (const p of g.projects ?? [])
          if (pset.has(String(p._id))) names.push(p.name);
      }
    }
    return names.join(', ');
  };

  const handleClick = fav => {
    switch (fav.type) {
      case 'Safety Walk':
      case 'DASH Report':
        history.push({
          pathname: '/app/safetyWalkContainer',
          state: {
            favGroups: fav?.groupIds,
            favAreas: fav?.projectIds,
            fromScreen: '/app/dashboard/'
          }
        });
        break;
      case 'Safety Audit':
      case 'Quality Audit':
      case 'Lockout/Tagout Procedure':
        const auditType =
          fav.type === 'Safety Audit'
            ? 'safety'
            : fav.type === 'Quality Audit'
              ? 'quality'
              : 'loto';
        history.push({
          pathname: `/app/audits/${auditType}/create`,
          state: {
            favTemplate: fav?.template,
            favGroups: fav?.groupIds,
            favAreas: fav?.projectIds,
            fromScreen: '/app/dashboard'
          }
        });
        break;
      case 'Custom Document':
        history.push({
          pathname: `/app/customDocumentContainer/${fav?.template?.value}`,
          state: {
            favTemplate: fav?.template,
            favGroups: fav?.groupIds,
            favAreas: fav?.projectIds,
            fromScreen: '/app/dashboard'
          }
        });
        break;
      case 'Behavior Observation':
        history.push({
          pathname: '/app/behaviorObservation/container',
          state: {
            favTemplate: fav?.template,
            favGroups: fav?.groupIds,
            favAreas: fav?.projectIds
          }
        });
        break;
      default:
        return;
    }
  };

  return (
    <Card
      className={classnames(styles.card, className)}
      title={card?.name}
      showHeader
      wide
    >
      {data === undefined ? (
        <Loading />
      ) : data.length === 0 ? (
        <div className={styles.empty}>No favorites set.</div>
      ) : (
        sliced.map(fav => {
          const isNoTemplateType =
            fav?.type === 'Safety Walk' || fav?.type === 'DASH Report';
          const groupText = getGroupNames(fav?.groupIds);
          const areaText = getProjectNames(fav?.groupIds, fav?.projectIds);

          const title = (
            <div className={styles.favTitleRow}>
              <span className={styles.favType}>{fav?.type}</span>
            </div>
          );

          const rows = (
            <div className={styles.favRows}>
              {!isNoTemplateType && (
                <div className={styles.favRow}>
                  <span className={styles.favLabel}>Template</span>
                  <span className={styles.favValue}>
                    {fav?.template?.label}
                  </span>
                </div>
              )}
              <div className={styles.favRow}>
                <span className={styles.favLabel}>Group/Establishment(s)</span>
                <span className={styles.favValue}>{groupText || '--'}</span>
              </div>
              <div className={styles.favRow}>
                <span className={styles.favLabel}>Area(s)</span>
                <span className={styles.favValue}>{areaText || '--'}</span>
              </div>
            </div>
          );

          return (
            <>
              <div
                key={`${fav.type}-${groupText}-${areaText}`}
                className={bStyles('favorite')}
                onClick={() => handleClick(fav)}
              >
                <div className={styles.left}>
                  {title}
                  {rows}
                </div>
                <div className={styles.right}>
                  <BsCaretRightFill size={24} className={styles.caretBtn} />
                </div>
              </div>
              <div className={styles.linebreaker} />
            </>
          );
        })
      )}
      <Pagination
        page={page}
        setPage={setPage}
        perPage={perPage}
        count={data?.length}
        className={styles.pagination}
      />
    </Card>
  );
}
